/* eslint-disable react/forbid-prop-types */
import { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Alert,
    Box,
    Chip,
    CircularProgress,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import { callApi } from 'src/utils/apiCall';
import { common } from 'src/theme/palette';
import WhatsAppTemplatePreviewPanel from 'src/components/whatsappComponents/WhatsAppTemplatePreviewPanel';
import SandboxedHtmlPreview from './SandboxedHtmlPreview';

const DEBOUNCE_MS = 250;
const VISIBLE_CHIPS = 8;

const PLACEHOLDER_COPY = 'Pick a template to see the preview — the link is already merged in.';
const ERROR_COPY = "Couldn't load preview — your send will still work.";

/**
 * SendFormsPreviewPanel — server-rendered template preview used by the Send
 * Forms Step-4 composer.
 *
 * Replaces the inline `resolveMergeTags` regex that previously lived in
 * QuickSendStep4Deliver.jsx. That regex knew 5 of the ~25 BE template-context
 * keys, so most variables rendered literally in the preview (the user-reported
 * "$expires_in_hours hours" repro). This panel calls the PR-2 preview
 * endpoints and lets the BE renderer be the single source of truth — the
 * preview now matches what the actual send will produce.
 *
 *   EMAIL    → GET /api/v1/practices/:practiceId/engagement-templates/:id/preview/
 *   WHATSAPP → GET /api/v1/practices/:practiceId/whatsapp-business-accounts/:waba/whatsapp-message-templates/:id/preview/
 *
 * The endpoint shape (per the architect doc §3.4.2) is:
 *   { subject, html, plaintext, used_variables, unresolved_variables }   // EMAIL
 *   { header, body, footer, buttons, used_variables, unresolved_variables } // WHATSAPP
 *
 * Loading / error / empty states are all explicit. The "unresolved_variables"
 * chip row is the cheapest way to surface authoring drift — if a template
 * references $foo and the BE context doesn't expose `foo`, the author sees a
 * chip with a tooltip explaining the consequence.
 */
const SendFormsPreviewPanel = ({
    practiceId,
    channel,
    templateId,
    whatsappBusinessAccountId,
    leadId,
    formId,
    crId,
    consentToken,
    height,
    onPreviewMeta,
    // Recipient + sender shown in the email-client-style header. Optional —
    // when absent the header collapses to just the Subject row so the
    // component still works in older callers that haven't been updated.
    recipientName,
    recipientEmail,
    senderName,
}) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [data, setData] = useState(null);

    // Debounce timer + a request id so a stale resolve can't overwrite the
    // newest one (prop-churn racing).
    const reqIdRef = useRef(0);

    // Keep the latest `onPreviewMeta` in a ref so the fetch-effect's
    // dependency array stays narrow (URL-only). Parents typically pass an
    // inline closure that changes identity every render; depending on it
    // directly would re-fire the debounced fetch unnecessarily.
    const onPreviewMetaRef = useRef(onPreviewMeta);
    useEffect(() => { onPreviewMetaRef.current = onPreviewMeta; }, [onPreviewMeta]);

    // URL builder memoised on every meaningful prop so we don't refire the
    // debounced fetch when an unrelated parent rerender happens.
    const previewUrl = useMemo(() => {
        if (!templateId || !practiceId) return null;
        if (channel === 'WHATSAPP' && !whatsappBusinessAccountId) return null;

        const params = new URLSearchParams();
        if (leadId != null && leadId !== '') params.set('lead_id', String(leadId));
        if (formId != null && formId !== '') params.set('form_id', String(formId));
        if (crId != null && crId !== '') params.set('cr_id', String(crId));
        if (consentToken) params.set('consent_token', consentToken);
        const qs = params.toString();
        const suffix = qs ? `?${qs}` : '';

        if (channel === 'EMAIL') {
            return `/api/v1/practices/${practiceId}/engagement-templates/${templateId}/preview/${suffix}`;
        }
        // WHATSAPP
        return `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${whatsappBusinessAccountId}/whatsapp-message-templates/${templateId}/preview/${suffix}`;
    }, [practiceId, channel, templateId, whatsappBusinessAccountId, leadId, formId, crId, consentToken]);

    useEffect(() => {
        // No template selected → render placeholder, skip the fetch entirely.
        if (!previewUrl) {
            setData(null);
            setError(null);
            setLoading(false);
            return undefined;
        }

        let cancelled = false;
        const myReqId = reqIdRef.current + 1;
        reqIdRef.current = myReqId;

        // Pre-set loading=true IMMEDIATELY so the 250ms debounce window
        // doesn't render with loading=false / data=null / error=null all
        // at once — which previously fell through to the yellow "Couldn't
        // load preview" Alert for a single frame when the user first
        // landed on the Email/WhatsApp tab. Clear any prior error too so
        // the loading state isn't masked by a stale warning.
        setLoading(true);
        setError(null);

        const timer = setTimeout(async () => {
            // setLoading(true) already ran synchronously above; the
            // debounced body still re-asserts it for the (rare) case where
            // the previous request resolved between the effect start and
            // the timer firing.
            setLoading(true);
            setError(null);
            try {
                const res = await callApi(previewUrl, 'GET');
                if (cancelled || reqIdRef.current !== myReqId) return;
                if (res?.status === 200 && res.data) {
                    setData(res.data);
                    // PR-10 item 11 — surface Mailgun-template metadata to
                    // the parent (super-admin "Open in Mailgun" link).
                    // Best-effort callback; only fires when the BE actually
                    // returns the flag. Wrapped in try/catch so a thrown
                    // parent handler can't kill the preview render.
                    const onMeta = onPreviewMetaRef.current;
                    if (typeof onMeta === 'function') {
                        try {
                            onMeta({
                                is_mailgun_template: Boolean(res.data.is_mailgun_template),
                                template_id: res.data.template_id || null,
                            });
                        } catch {
                            // best-effort — parent failure shouldn't break
                            // the preview pane.
                        }
                    }
                } else {
                    setData(null);
                    setError(res?.data?.detail || 'preview_failed');
                }
            } catch (err) {
                if (cancelled || reqIdRef.current !== myReqId) return;
                setData(null);
                setError(err?.message || 'preview_failed');
            } finally {
                if (!cancelled && reqIdRef.current === myReqId) {
                    setLoading(false);
                }
            }
        }, DEBOUNCE_MS);

        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [previewUrl]);

    // ── Render branches ───────────────────────────────────────────────────

    // 1. No template id → small dashed placeholder. Copy is lifted verbatim
    //    from the previous Step-4 placeholder so we don't regress.
    if (!templateId) {
        return (
            <Box
                sx={{
                    p: 1.5,
                    borderRadius: '8px',
                    border: '1px dashed',
                    borderColor: 'rgba(145,158,171,0.32)',
                    bgcolor: 'rgba(145,158,171,0.04)',
                    textAlign: 'center',
                }}
            >
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '11.5px' }}>
                    {PLACEHOLDER_COPY}
                </Typography>
            </Box>
        );
    }

    // 2. Loading.
    if (loading) {
        return (
            <Stack direction="row" alignItems="center" gap={1} sx={{ p: 1.5 }}>
                <CircularProgress size={14} sx={{ color: common.mintyZest }} />
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '11.5px' }}>
                    Loading preview…
                </Typography>
            </Stack>
        );
    }

    // 3. Error — only when the fetch ACTUALLY failed. The previous check
    //    `error || !data` rendered the yellow warning during the debounce
    //    window (data null, error null, loading momentarily false on first
    //    mount), causing a 1-frame flash before the loading spinner took
    //    over. We now require an explicit error AND fall through to a
    //    quiet loading skeleton if data hasn't arrived yet for any other
    //    reason.
    if (error) {
        return (
            <Alert
                severity="warning"
                sx={{
                    borderRadius: '8px',
                    fontSize: '12px',
                    '& .MuiAlert-message': { fontSize: '12px' },
                }}
            >
                {ERROR_COPY}
            </Alert>
        );
    }

    // 3b. Data not yet available — render the same loading affordance as
    //     branch 2 instead of warning. Belt-and-braces in case any caller
    //     manages to clear `data` without setting `loading`.
    if (!data) {
        return (
            <Stack direction="row" alignItems="center" gap={1} sx={{ p: 1.5 }}>
                <CircularProgress size={14} sx={{ color: common.mintyZest }} />
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '11.5px' }}>
                    Loading preview…
                </Typography>
            </Stack>
        );
    }

    // 4. Success — render channel-specific preview body and the unresolved
    //    variables chip row underneath.
    const unresolved = Array.isArray(data.unresolved_variables) ? data.unresolved_variables : [];

    return (
        <Stack gap={1}>
            {channel === 'EMAIL' ? (
                <Box
                    sx={{
                        borderRadius: '10px',
                        border: '1px solid',
                        borderColor: 'rgba(145,158,171,0.24)',
                        bgcolor: '#fff',
                        overflow: 'hidden',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
                    }}
                >
                    {/* Email-client-style metadata header — From / To / Subject.
                        Mirrors the way Gmail/Outlook lay out the header of a
                        thread preview so the user can verify all three lines
                        at a glance before sending. Subject row is always
                        rendered (with a quiet placeholder when the BE preview
                        omits one); From/To rows render only when the parent
                        passes the relevant prop. */}
                    <Box
                        sx={{
                            bgcolor: 'rgba(145,158,171,0.04)',
                            borderBottom: '1px solid rgba(145,158,171,0.16)',
                            px: 1.75,
                            py: 1.25,
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: '14px',
                                fontWeight: 700,
                                color: '#212B36',
                                lineHeight: 1.35,
                                mb: (senderName || recipientEmail) ? 0.625 : 0,
                                wordBreak: 'break-word',
                            }}
                        >
                            {data.subject || (
                                <Box
                                    component="span"
                                    sx={{ color: 'text.disabled', fontStyle: 'italic', fontWeight: 500 }}
                                >
                                    (no subject)
                                </Box>
                            )}
                        </Typography>
                        {(senderName || recipientEmail) && (
                            <Stack gap={0.25}>
                                {senderName && (
                                    <Stack direction="row" gap={0.75} alignItems="baseline">
                                        <Typography
                                            sx={{
                                                fontSize: '10.5px',
                                                fontWeight: 700,
                                                letterSpacing: '0.04em',
                                                textTransform: 'uppercase',
                                                color: 'text.disabled',
                                                minWidth: 38,
                                            }}
                                        >
                                            From
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontSize: '12px',
                                                color: '#454F5B',
                                                lineHeight: 1.4,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {senderName}
                                        </Typography>
                                    </Stack>
                                )}
                                {recipientEmail && (
                                    <Stack direction="row" gap={0.75} alignItems="baseline">
                                        <Typography
                                            sx={{
                                                fontSize: '10.5px',
                                                fontWeight: 700,
                                                letterSpacing: '0.04em',
                                                textTransform: 'uppercase',
                                                color: 'text.disabled',
                                                minWidth: 38,
                                            }}
                                        >
                                            To
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontSize: '12px',
                                                color: '#454F5B',
                                                lineHeight: 1.4,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {recipientName ? `${recipientName} <${recipientEmail}>` : recipientEmail}
                                        </Typography>
                                    </Stack>
                                )}
                            </Stack>
                        )}
                    </Box>
                    {/* Body — sandboxed in an iframe so the template's CSS
                        can't leak into the host page. */}
                    <Box sx={{ p: 1.25, bgcolor: '#fff' }}>
                        <SandboxedHtmlPreview
                            html={data.html || ''}
                            height={height}
                            ariaLabel="Email body preview"
                        />
                    </Box>
                </Box>
            ) : (
                (() => {
                    // Adapter — see PR-3 §6 note. The WA panel takes a
                    // `template` object with .components[] / .header /
                    // .body / .footer (all STRINGS). The PR-2 endpoint
                    // returns:
                    //   header: {type:'TEXT'|'DOCUMENT'|'IMAGE', text?, media_url?}
                    //   body:   string
                    //   footer: string
                    //   buttons:[...]
                    // The header is an OBJECT — we have to extract the
                    // displayable string before packing, otherwise the
                    // panel's parseWhatsappMarkup calls .split on an
                    // object and crashes (TypeError: e.split is not a
                    // function — caught by the in-prod error boundary).
                    const rawHeader = data.header;
                    let headerText = '';
                    if (typeof rawHeader === 'string') {
                        headerText = rawHeader;
                    } else if (rawHeader && typeof rawHeader === 'object') {
                        if (rawHeader.type === 'TEXT') {
                            headerText = typeof rawHeader.text === 'string'
                                ? rawHeader.text : '';
                        } else if (rawHeader.type === 'DOCUMENT') {
                            // Media headers don't have body text — surface
                            // the filename so the preview shows *something*
                            // representative of what the recipient gets.
                            headerText = rawHeader.text
                                || (rawHeader.media_url ? '📎 PDF attachment' : '');
                        } else if (rawHeader.type === 'IMAGE') {
                            headerText = rawHeader.text
                                || (rawHeader.media_url ? '🖼 Image' : '');
                        }
                    }
                    const bodyText = typeof data.body === 'string' ? data.body : '';
                    const footerText = typeof data.footer === 'string' ? data.footer : '';

                    return (
                        <WhatsAppTemplatePreviewPanel
                            template={{
                                name: 'preview',
                                usage_type: 'FORMS',
                                header: headerText,
                                body: bodyText,
                                footer: footerText,
                                components: [
                                    ...(headerText ? [{ type: 'HEADER', text: headerText }] : []),
                                    { type: 'BODY', text: bodyText },
                                    ...(footerText ? [{ type: 'FOOTER', text: footerText }] : []),
                                    ...(Array.isArray(data.buttons) && data.buttons.length
                                        ? [{ type: 'BUTTONS', buttons: data.buttons }]
                                        : []),
                                ],
                            }}
                            showVariables={false}
                            hideTitle
                        />
                    );
                })()
            )}

            {unresolved.length > 0 && (
                <Box>
                    <Typography
                        sx={{
                            fontSize: '10.5px', fontWeight: 700, letterSpacing: '0.04em',
                            textTransform: 'uppercase', color: 'text.secondary', mb: 0.5,
                        }}
                    >
                        Unresolved variables:
                    </Typography>
                    <Stack direction="row" flexWrap="wrap" gap={0.5}>
                        {unresolved.slice(0, VISIBLE_CHIPS).map((name) => (
                            <Tooltip
                                key={name}
                                title={`This template references $${name} but the system doesn't have a value for it — it will appear blank in the email.`}
                            >
                                <Chip
                                    size="small"
                                    variant="outlined"
                                    label={`$${name}`}
                                    sx={{
                                        height: 22,
                                        fontSize: '11px',
                                        '& .MuiChip-label': { px: 0.75 },
                                    }}
                                />
                            </Tooltip>
                        ))}
                        {unresolved.length > VISIBLE_CHIPS && (
                            <Tooltip
                                title={unresolved
                                    .slice(VISIBLE_CHIPS)
                                    .map((name) => `$${name}`)
                                    .join(', ')}
                            >
                                <Chip
                                    size="small"
                                    variant="outlined"
                                    label={`+${unresolved.length - VISIBLE_CHIPS} more`}
                                    sx={{
                                        height: 22,
                                        fontSize: '11px',
                                        '& .MuiChip-label': { px: 0.75 },
                                    }}
                                />
                            </Tooltip>
                        )}
                    </Stack>
                </Box>
            )}
        </Stack>
    );
};

SendFormsPreviewPanel.propTypes = {
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    channel: PropTypes.oneOf(['EMAIL', 'WHATSAPP']).isRequired,
    templateId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    // Required when channel === 'WHATSAPP'. PropTypes can't express that
    // conditionally without a custom validator, so do it inline.
    whatsappBusinessAccountId(props, propName, componentName) {
        if (props.channel === 'WHATSAPP') {
            const v = props[propName];
            if (v == null || v === '') {
                return new Error(
                    `${componentName}: prop "${propName}" is required when channel === "WHATSAPP"`,
                );
            }
        }
        return null;
    },
    leadId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    formId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    crId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    consentToken: PropTypes.string,
    height: PropTypes.number,
    // PR-10 item 11 — fires once after the preview API resolves with
    // `{ is_mailgun_template, template_id }`. Parent uses this to render
    // the super-admin "Open in Mailgun" link.
    onPreviewMeta: PropTypes.func,
    recipientName: PropTypes.string,
    recipientEmail: PropTypes.string,
    senderName: PropTypes.string,
};

SendFormsPreviewPanel.defaultProps = {
    templateId: null,
    whatsappBusinessAccountId: null,
    leadId: null,
    formId: null,
    crId: null,
    consentToken: null,
    height: 240,
    onPreviewMeta: null,
    recipientName: '',
    recipientEmail: '',
    senderName: '',
};

export default SendFormsPreviewPanel;
